/* eslint-env browser */
// Settings › Live event (wave 3, W2_Portal_Settings look): the paid live
// stream. The event fields live on /settings; the stream URL lives in the
// admin-only /event_settings node (read once when the tab opens, as before).
//
// Save does what the old Submit did, in the same order: check the fields,
// upload a newly chosen poster (uploadEventImage), write the WHOLE settings
// object with the event fields through editSettings, then write
// { streamUrl } through editEventSettings. Only with critical edits on.
// The event date is entered in Addis Ababa time on a 12-hour clock and is
// stored as before (epoch ms).
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, SaveBar, AddisDateTimeField } from '../components/modern';

// The event saved here survives switching Settings tabs for the rest of the
// session while the store still holds the settings it started from.
let savedCache = null; // { source, values }

const KEYS = ['eventStreamEnabled', 'eventId', 'eventName', 'eventDate', 'eventFee', 'eventImage'];

const valuesFrom = (s) => ({
  eventStreamEnabled: s.eventStreamEnabled === true,
  eventId: s.eventId || '',
  eventName: s.eventName || '',
  eventDate: s.eventDate || null,
  eventFee: s.eventFee || s.eventFee === 0 ? s.eventFee : '',
  eventImage: s.eventImage || '',
});
const formFor = (source) => {
  if (!source) return null;
  if (savedCache && savedCache.source === source) return savedCache.values;
  return valuesFrom(source);
};
const text = (v) => (v === undefined || v === null ? '' : String(v));

// Wide screens (lg and up): the event settings in two columns, the poster
// across the row; one column below lg as before.
const ROWS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
};

const LiveEventSettings = () => {
  const { t, i18n } = useTranslation();
  const { editSettings } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const source = settingsdata.settings;

  const [base, setBase] = useState(() => formFor(source));
  const [data, setData] = useState(() => formFor(source));
  const [streamUrl, setStreamUrl] = useState('');
  const [baseStreamUrl, setBaseStreamUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const sourceRef = useRef(source);
  const fileRef = useRef(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(formFor(source));
      setData(formFor(source));
    }
  }, [source]);

  useEffect(() => {
    // streamUrl lives in admin-only /event_settings, not the public /settings
    api.fetchEventSettings()
      .then((es) => {
        if (!aliveRef.current) return;
        setStreamUrl(es.streamUrl || '');
        setBaseStreamUrl(es.streamUrl || '');
      })
      .catch(() => {});
  }, []);

  const changed = useMemo(() => {
    if (!data || !base) return [];
    const out = KEYS.filter((k) => text(data[k]) !== text(base[k]));
    if (streamUrl !== baseStreamUrl) out.push('streamUrl');
    if (imageFile) out.push('image');
    return out;
  }, [data, base, streamUrl, baseStreamUrl, imageFile]);

  const setField = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) {
      setData(base);
      setStreamUrl(baseStreamUrl);
      clearImage();
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    if (saving || !data) return;
    const latest = settingsdata.settings;
    if (!latest) return;
    if (!latest.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const eventId = (data.eventId || '').trim();
    const fee = parseFloat(data.eventFee);
    const url = (streamUrl || '').trim();
    // eventId becomes an RTDB path segment (event_payments/{eventId}/...)
    if (eventId && !/^[A-Za-z0-9_-]+$/.test(eventId)) {
      const msg = t('live_event_id_invalid', { defaultValue: 'Event ID may only contain letters, numbers, hyphens and underscores.' });
      setErrors({ eventId: msg });
      showToast.error(msg);
      return;
    }
    if (data.eventStreamEnabled && (!eventId || !data.eventDate || !(fee > 0) || !url)) {
      const msg = t('live_event_fields_required', { defaultValue: 'Event ID, date, fee and stream URL are all required to enable the live event.' });
      const required = t('form_required', { defaultValue: 'This field is required' });
      setErrors({
        eventId: eventId ? null : required,
        eventDate: data.eventDate ? null : required,
        eventFee: fee > 0 ? null : required,
        streamUrl: url ? null : required,
      });
      showToast.error(msg);
      return;
    }
    if (imageFile && !eventId) {
      const msg = t('live_event_image_needs_id', { defaultValue: 'Set an Event ID before uploading an image.' });
      setErrors({ eventId: msg });
      showToast.error(msg);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      let eventImage = data.eventImage || '';
      if (imageFile) {
        eventImage = await api.uploadEventImage(eventId, imageFile);
        clearImage();
      }
      const values = {
        eventStreamEnabled: data.eventStreamEnabled === true,
        eventId: eventId,
        eventName: data.eventName || '',
        eventDate: data.eventDate ? Number(data.eventDate) : null,
        eventFee: fee > 0 ? fee : 0,
        eventImage: eventImage,
      };
      const done = dispatch(editSettings({ ...latest, ...values }));
      api.editEventSettings({ streamUrl: url });
      await done;
      savedCache = { source: sourceRef.current, values };
      if (aliveRef.current) {
        setBase(values);
        setData(values);
        setBaseStreamUrl(url);
        setStreamUrl(url);
        showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
      }
    } catch (err) {
      if (aliveRef.current) showToast.error(t('live_event_image_upload_failed', { defaultValue: 'Image upload failed. Please try again.' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  if (!data) return <CircularLoading inline />;

  const inputSx = { width: { xs: '100%', sm: 420 } };
  const poster = imagePreview || data.eventImage;

  return (
    <Box dir={i18n.dir()}>
      <SectionCard
        id="live"
        icon={<LiveTvOutlinedIcon />}
        hue="info"
        title={t('live_event', { defaultValue: 'Live event' })}
        sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
      >
        <Box sx={{ mt: 1, pb: 1, ...ROWS_SX }}>
          <SettingRow
            id="live-enabled"
            layout="switch"
            label={t('live_event_enable', { defaultValue: 'Enable live event' })}
            changed={changed.includes('eventStreamEnabled')}
            control={
              <Switch id="live-enabled" checked={data.eventStreamEnabled === true} onChange={(e) => setField('eventStreamEnabled', e.target.checked)} sx={{ my: -0.75 }} />
            }
          />
          <SettingRow
            id="live-eventId"
            label={t('live_event_id', { defaultValue: 'Event ID (slug)' })}
            hint={t('live_event_id_help', { defaultValue: 'Changing this starts a new event — previous payments will no longer grant access.' })}
            changed={changed.includes('eventId')}
            error={errors.eventId}
            control={
              <TextField
                id="live-eventId"
                size="small"
                value={data.eventId}
                error={!!errors.eventId}
                onChange={(e) => setField('eventId', e.target.value)}
                inputProps={{ spellCheck: false, autoComplete: 'off', 'aria-describedby': 'live-eventId-hint live-eventId-error' }}
                sx={inputSx}
              />
            }
          />
          <SettingRow
            id="live-eventName"
            label={t('live_event_name', { defaultValue: 'Event name' })}
            changed={changed.includes('eventName')}
            control={<TextField id="live-eventName" size="small" value={data.eventName} onChange={(e) => setField('eventName', e.target.value)} sx={inputSx} />}
          />
          <SettingRow
            id="live-eventDate"
            label={t('live_event_date', { defaultValue: 'Event date & time' })}
            hint={t('live_event_ends', { defaultValue: 'Access ends 24 hours after the event starts.' })}
            changed={changed.includes('eventDate')}
            error={errors.eventDate}
            control={
              <AddisDateTimeField
                id="live-eventDate"
                value={data.eventDate}
                onChange={(ms) => setField('eventDate', ms)}
                error={!!errors.eventDate}
                describedBy="live-eventDate-hint live-eventDate-error"
              />
            }
          />
          <SettingRow
            id="live-eventFee"
            label={t('live_event_fee', { defaultValue: 'Access fee' })}
            changed={changed.includes('eventFee')}
            error={errors.eventFee}
            control={
              <TextField
                id="live-eventFee"
                size="small"
                type="number"
                value={data.eventFee}
                error={!!errors.eventFee}
                onChange={(e) => setField('eventFee', parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : '')}
                inputProps={{ min: 0, inputMode: 'decimal' }}
                sx={{ width: { xs: '100%', sm: 200 }, '& input': { fontVariantNumeric: 'tabular-nums' } }}
              />
            }
          />
          <SettingRow
            id="live-streamUrl"
            label={t('live_event_stream_url', { defaultValue: 'Stream URL (embeddable page)' })}
            hint={t('live_event_stream_url_help', { defaultValue: 'Only revealed to users who have paid. Must be embeddable in a mobile WebView.' })}
            changed={changed.includes('streamUrl')}
            error={errors.streamUrl}
            control={
              <TextField
                id="live-streamUrl"
                size="small"
                value={streamUrl}
                error={!!errors.streamUrl}
                onChange={(e) => {
                  setStreamUrl(e.target.value);
                  if (errors.streamUrl) setErrors((x) => ({ ...x, streamUrl: null }));
                }}
                inputProps={{ inputMode: 'url', spellCheck: false, autoComplete: 'off', 'aria-describedby': 'live-streamUrl-hint live-streamUrl-error' }}
                sx={inputSx}
              />
            }
          />
          <SettingRow
            id="live-image"
            layout="stack"
            sx={{ gridColumn: '1 / -1' }}
            label={t('live_event_image', { defaultValue: 'Event image (poster)' })}
            hint={t('live_event_image_help', { defaultValue: 'Shown on the pay-to-watch screen in the app. Landscape (16:9) works best. Uploaded when you press Submit.' })}
            changed={changed.includes('image')}
            control={
              <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                {poster ? (
                  <Box
                    component="img"
                    src={poster}
                    alt={t('live_event_image', { defaultValue: 'Event image (poster)' })}
                    sx={{ maxWidth: 320, width: '100%', borderRadius: '10px', border: `1px solid ${c.OUTLINE}`, display: 'block' }}
                  />
                ) : (
                  <Box sx={{ width: 220, height: 124, borderRadius: '10px', border: `1px dashed ${c.OUTLINE_STRONG}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.TEXT_3 }}>
                    <ImageOutlinedIcon aria-hidden="true" />
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Button variant="outlined" color="neutral" component="label" startIcon={<ImageOutlinedIcon />}>
                    {t('live_event_choose_image', { defaultValue: 'Choose image' })}
                    <input
                      ref={fileRef}
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files && event.target.files[0];
                        setImageFile(file || null);
                        setImagePreview(file ? URL.createObjectURL(file) : null);
                      }}
                    />
                  </Button>
                  {imageFile ? (
                    <Typography variant="body2" sx={{ color: c.INK_2, overflowWrap: 'anywhere', maxWidth: 260 }}>
                      {imageFile.name}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            }
          />
        </Box>
        <SaveBar
          changedCount={changed.length}
          errorCount={Object.values(errors).filter(Boolean).length}
          onJumpToError={() => {
            const key = Object.keys(errors).find((k) => errors[k]);
            const el = key ? document.getElementById(`live-${key}`) : null;
            if (el && typeof el.focus === 'function') el.focus();
          }}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default LiveEventSettings;
